import { youboraPlugin } from '@/Components/VideoPlayer/Core/YouboraPlayerConfig';
import useMedia from '@/Hooks/useMedia';
import usePersistCallback from '@/Hooks/usePersistCallback';
import { actions, selectors } from '@/Store';
import brightCovePlayerLoader from '@brightcove/player-loader';
import { PlaybackMeta } from '@skytvnz/sky-app-store/lib/types/models/PlaybackMeta';
import { F } from 'ramda';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Sentry from '@/Utils/Sentry';
import { SsaiEventType } from '@/Utils/SSAI';
import BrightCoveControllerFactory from './BrightCoveControllerFactory';
import { destroyMediaTailorSession } from './DataZoomMediaTailorConfig';
import MetaLoader, { createResolve } from './MetaLoader';
import { PlayerController, PlayerProps } from './PlayerTypes';
import YouboraVideoJsAdapter from './YouboraVideoJsAdapter';

export const BRIGHTCOVE_ACCOUNT_ID = process.env.BRIGHTCOVE_ACCOUNT_ID || '';
export const BRIGHTCOVE_PLAYER_ID = process.env.BRIGHTCOVE_PLAYER_ID || '';

export const PLAYER_USER_INACTIVE_TIMEOUT = 5000; // in ms
export const PLAYER_DURATION_JUMP = 10; // in seconds
export const PLAYER_VOLUME_DEFAULT = 1;
export const PLAYER_VOLUME_UNMUTE = 0.05;
export const PLAYER_VOLUME_JUMP = 0.05;
export const POSTER_CLASSNAME = 'poster-visible';

const posterImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const videojsOptions = {
  autoplay: false,
  userActions: {
    hotkeys: {
      // Disable the VideoJS default shortcut events to use our own customize shortcut
      muteKey: F,
      fullscreenKey: F,
      playPauseKey: F,
    },
  },
  inactivityTimeout: PLAYER_USER_INACTIVE_TIMEOUT,
};

window.Plugin = youboraPlugin;

const BrightCovePlayer: FC<PlayerProps> = ({
  playbackMeta,
  config,
  isLivePlayer,
  className,
  width,
  height,
  youboraPlayerConfig,
  setIsAdPlaying,
  ...events
}) => {
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const playerRef = useRef<any>();
  const playerControllerRef = useRef<PlayerController>();
  const playbackMetaResolve = useRef(createResolve<PlaybackMeta>());
  const accessToken = useSelector(selectors.auth.accessToken);
  const dispatch = useDispatch();
  const { isMediaM } = useMedia();
  const {
    onError,
    onEnded,
    onDataLoaded,
    onPlay,
    onPause,
    onBuffering,
    onTimeUpdate,
    onAdTimeUpdate,
    onWaiting,
    onPlaying,
    onSeeking,
    onSeeked,
    onSsaiAdsPlaying,
  } = events;

  /* Player events init */
  const initPlayerEvents = usePersistCallback(() => {
    const { current: player } = playerRef;
    player.on('loadeddata', onDataLoaded);
    player.on('ended', onEnded);
    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('waiting', onWaiting);
    player.on('playing', onPlaying);
    player.on('timeupdate', onTimeUpdate);
    player.on('adtimeupdate', onAdTimeUpdate);
    player.on('progress', onBuffering);
    player.on('seeking', onSeeking);
    player.on('seeked', onSeeked);
    player.on('error', onError);
    // SSAI
    player.on(SsaiEventType.PodStarted, onSsaiAdsPlaying);
    player.on(SsaiEventType.PodEnded, onSsaiAdsPlaying);
    player.on(SsaiEventType.AdStarted, onSsaiAdsPlaying);
    player.on(SsaiEventType.AdEnded, onSsaiAdsPlaying);
    player.on(SsaiEventType.AdPause, onSsaiAdsPlaying);
    player.on(SsaiEventType.AdPlay, onSsaiAdsPlaying);
  });

  /* Player events removal */
  const disposeEvents = usePersistCallback(() => {
    const { current: player } = playerRef;
    if (player) {
      player.off('loadeddata', onDataLoaded);
      player.off('ended', onEnded);
      player.off('play', onPlay);
      player.off('pause', onPause);
      player.off('waiting', onWaiting);
      player.off('playing', onPlaying);
      player.off('timeupdate', onTimeUpdate);
      player.off('adtimeupdate', onAdTimeUpdate);
      player.off('progress', onBuffering);
      player.off('seeking', onSeeking);
      player.off('seeked', onSeeked);
      player.off('error', onError);
      // SSAI
      player.off(SsaiEventType.PodStarted, onSsaiAdsPlaying);
      player.off(SsaiEventType.PodEnded, onSsaiAdsPlaying);
      player.off(SsaiEventType.AdStarted, onSsaiAdsPlaying);
      player.off(SsaiEventType.AdEnded, onSsaiAdsPlaying);
      player.off(SsaiEventType.AdPause, onSsaiAdsPlaying);
      player.off(SsaiEventType.AdPlay, onSsaiAdsPlaying);
    }
  });

  const dispatchSubTitleTrack = useCallback(
    (playbackMetaData, player) => {
      const subTitleTrackFromPlayer = player.textTracks().tracks_;
      const subTitleTrack = subTitleTrackFromPlayer
        .filter(track => track?.kind === 'subtitles')
        .map(({ label, language }) => {
          let updatedLabel = label;
          if (language === 'en') {
            updatedLabel = 'English';
          }
          return { label: updatedLabel, language };
        });
      // safari is using brightcove for live content,sending playbackmetaData?.id for live content playing in safari
      dispatch(
        actions.playback.setTextTracks(
          playbackMetaData?.mediaAssetId ?? playbackMetaData?.id,
          subTitleTrack.length ? (subTitleTrack as any) : [],
        ),
      );
    },
    [dispatch],
  );

  /**
   * Effect to handle component mount & mount. creates a shaka.Player instance.
   * As BC loader is dynamic loading the brightCove player, and in the meantime the playbackMeta is potential loading
   * So need to wait all async process to be done.
   */
  useEffect(() => {
    // Dynamic loading the player script from Brightcove CDN
    if (!BRIGHTCOVE_ACCOUNT_ID || !BRIGHTCOVE_PLAYER_ID) {
      Sentry.captureMessage('Brightcove account ID or player ID is not set.');
      return () => {};
    }
    const playerLoader = (async () => {
      try {
        const { ref: player } = await brightCovePlayerLoader({
          refNode: videoWrapperRef.current,
          accountId: BRIGHTCOVE_ACCOUNT_ID,
          playerId: BRIGHTCOVE_PLAYER_ID,
          embedId: 'default',
          options: isLivePlayer ? videojsOptions : { ...videojsOptions, poster: posterImage },
        });
        playerRef.current = player;
        youboraPlugin.setAdapter(new YouboraVideoJsAdapter(playerRef.current));

        // Focus to the player, so that user can control it by keyboard immediately
        player.focus();
        // Do not trigger fullscreen native player on mobile
        player.playsinline(true);
        if (!isLivePlayer) {
          player.addClass(POSTER_CLASSNAME);
        }

        playerControllerRef.current = BrightCoveControllerFactory(
          player,
          player.$('video'),
          accessToken,
          setIsAdPlaying,
        );
        playerControllerRef.current.muted(true);
        events?.onPlayerLoaded?.(playerControllerRef.current, playerRef.current);
        initPlayerEvents();
      } catch (error) {
        Sentry.captureMessage(`Failed to load Brightcove player script: ${error}`);
      }
    })();

    // Waiting until player script and playbackMeta all loaded
    (async () => {
      try {
        const pendedMeta = await MetaLoader(playerLoader, playbackMetaResolve.current);
        playerControllerRef.current?.loadMeta?.(pendedMeta);
        setPlayerLoaded(true);
      } catch (error) {
        Sentry.captureMessage(`Failed to load playback metadata: ${error}`);
      }
    })();

    return () => {
      disposeEvents();
      destroyMediaTailorSession();
      playerRef.current?.dispose();
      playerRef.current = null;
      events?.onPlayerUnload?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (youboraPlayerConfig && youboraPlayerConfig?.content_id && playerRef.current) {
      youboraPlugin.setOptions(youboraPlayerConfig?.getOptions());
      // youbora has the annoying habit to overwrite content.id before sending the start event, hence we have to set data-video-id here.
      playerRef.current.options({
        'data-video-id': youboraPlayerConfig.getOptions()['content.id'],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youboraPlayerConfig, playerRef.current]);

  // Load the source url when we have one.
  useEffect(() => {
    const { current: player } = playerRef;
    if (
      (!isLivePlayer && !playbackMeta?.playbackSource) ||
      (isLivePlayer && !playbackMeta?.streamUri)
    ) {
      return;
    }

    if (player) {
      // If player has been initialized
      playerControllerRef.current?.loadMeta?.(playbackMeta);
      setPlayerLoaded(true);
    } else {
      // If player still loading, add into async racing
      playbackMetaResolve.current?.load(playbackMeta);
    }
  }, [playbackMeta, isLivePlayer]);

  useEffect(() => {
    const { current: player } = playerRef;
    if (player && playerLoaded) {
      player.ready(() => {
        player.textTracks().addEventListener('addtrack', function handleAddTrack() {
          dispatchSubTitleTrack(playbackMeta, player);
        });
      });
    }
  }, [dispatchSubTitleTrack, playerRef, playbackMeta, playerLoaded, isMediaM]);

  useEffect(() => {
    return () => {
      youboraPlugin._adapter?.stop();
      // Youbora is not catching the stop event from
      // Brightcove when we click on the back arrow button, so we are manually sending the event.
      // Without that we see an interrupted series of ping requests to youbora until you play something else.
      youboraPlugin._adapter?.fireStop();
    };
  }, []);

  return <div data-testid="brightcove-player" ref={videoWrapperRef} className={className} />;
};

export default BrightCovePlayer;
